import React from 'react';
import { Button, Progress, Table, Timeline } from 'antd';
import { Download, Percent, CircleCheck, History, ChevronDown } from 'lucide-react';
import { StatusTag, EmptyState, CollapsibleCard } from '../../../common';
import { semantic, neutral } from '../../../../theme/tokens';
import { useShareData, distributionTone } from './shareData.controller';

const toneColor = (percentage) => semantic[distributionTone(percentage)].solid;
const toneText = { success: 'text-success-fg', warning: 'text-warning-fg', danger: 'text-danger-fg' };

/** Quiet selectable pill for the store-type filter. */
const FilterChip = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`h-7 rounded-md border px-2.5 text-xs font-medium transition-colors ${
            active ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-surface-200 bg-white text-surface-600 hover:border-surface-300 hover:text-surface-900'
        }`}>
        {children}
    </button>
);

function ShareData({ checks, clientName, storeTypes, channel }) {
    const { filter, setFilter, latestStoreChecks, download, storeTypeOptions, toggleCategory, categories } = useShareData({
        checks,
        clientName,
        storeTypes,
        channel
    });

    const columns = [
        {
            title: 'Producto',
            dataIndex: 'product',
            key: 'product',
            width: '45%',
            render: (text) => <span className="text-sm text-surface-700">{text}</span>
        },
        {
            title: 'Disponibilidad',
            key: 'totals',
            width: '25%',
            align: 'right',
            render: (_, record) => (
                <span className="text-sm tabular-nums">
                    <strong className="text-surface-900">{record.count}</strong>
                    <span className="text-surface-400"> / {record.checks}</span>
                </span>
            ),
            sorter: (a, b) => a.count - b.count
        },
        {
            title: 'Distribución',
            dataIndex: 'percentage',
            key: 'percentage',
            width: '30%',
            render: (percentage) => (
                <div className="flex items-center gap-3">
                    <Progress percent={percentage} size="small" showInfo={false} strokeColor={toneColor(percentage)} className="!m-0 max-w-[100px] flex-1" />
                    <span className={`min-w-[40px] text-right text-sm font-semibold tabular-nums ${toneText[distributionTone(percentage)]}`}>{percentage}%</span>
                </div>
            ),
            sorter: (a, b) => a.percentage - b.percentage
        }
    ];

    return (
        <CollapsibleCard flush icon={<Percent />} iconTone="violet" title="Distribución Numérica" count={`${latestStoreChecks?.length || 0} Pisos`} countTone="violet">
            <div className="p-5">
                {/* Toolbar */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm text-surface-500">Análisis de disponibilidad de productos por categoría</span>
                    <Button icon={<Download />} onClick={download}>
                        Exportar Excel
                    </Button>
                </div>

                {/* Filter by Store Type (Traditional only) */}
                {channel === 'tradicional' && (
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="mr-1 text-xs font-medium text-surface-500">Filtrar por tipo de piso:</span>
                        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
                            Todos
                        </FilterChip>
                        {storeTypeOptions.map((store, i) => (
                            <FilterChip key={i} active={filter === store.value} onClick={() => setFilter(store.value)}>
                                {store.label}
                            </FilterChip>
                        ))}
                    </div>
                )}

                {/* Categories */}
                <div className="space-y-2">
                    {categories.map(({ index, category, categoryProducts, categoryPercentage, productsData, isOpen }) => {
                        const pct = Number(categoryPercentage);
                        const iconClass = pct >= 80 ? 'text-success-fg' : pct >= 50 ? 'text-warning-fg' : 'text-surface-400';
                        return (
                            <div key={index} className="overflow-hidden rounded-lg border border-surface-200">
                                {/* Category Header */}
                                <button
                                    type="button"
                                    aria-expanded={isOpen}
                                    className="flex w-full items-center justify-between gap-3 bg-surface-25 px-4 py-2.5 text-left transition-colors hover:bg-surface-50"
                                    onClick={() => toggleCategory(index)}>
                                    <span className="flex min-w-0 items-center gap-2.5">
                                        <CircleCheck className={`shrink-0 text-base ${iconClass}`} />
                                        <span className="truncate text-sm font-semibold text-surface-900">{category.value}</span>
                                        <StatusTag tone="neutral">{categoryProducts.length} productos</StatusTag>
                                    </span>
                                    <span className="flex items-center gap-3">
                                        <Progress
                                            type="circle"
                                            percent={parseFloat(categoryPercentage)}
                                            size={36}
                                            strokeWidth={9}
                                            format={(percent) => <span className="text-[10px] font-semibold tabular-nums text-surface-700">{`${percent}%`}</span>}
                                            strokeColor={toneColor(categoryPercentage)}
                                            trailColor={neutral[100]}
                                        />
                                        <ChevronDown className={`text-surface-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                                    </span>
                                </button>

                                {/* Category Content */}
                                {isOpen && (
                                    <div className="border-t border-surface-100">
                                        <Table columns={columns} dataSource={productsData} pagination={false} size="small" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Audit History */}
                <div className="mt-6 border-t border-surface-100 pt-5">
                    <div className="mb-4 flex items-center gap-2">
                        <History className="text-surface-500" />
                        <span className="text-sm font-semibold text-surface-900">Historial de Auditorías</span>
                    </div>
                    {latestStoreChecks?.length ? (
                        <Timeline
                            items={latestStoreChecks.slice(0, 10).map((check, idx) => ({
                                color: idx === 0 ? semantic.violet.solid : neutral[300],
                                children: (
                                    <div>
                                        <span className="block text-sm font-medium text-surface-900">{check.storeId?.name || 'Unknown Store'}</span>
                                        <span className="text-xs text-surface-500 tabular-nums">
                                            {new Date(check.startTime).toLocaleString('es-ES', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </span>
                                    </div>
                                )
                            }))}
                        />
                    ) : (
                        <EmptyState icon={<History />} title="Sin auditorías" description="No hay auditorías en el período seleccionado." className="!py-6" />
                    )}
                </div>
            </div>
        </CollapsibleCard>
    );
}

export default ShareData;
